/**
 * Detailed political geography for the map view: 10m-scale country fills and
 * region borders served by /world/admin-areas. This replaces the bundled
 * 110m atlas below continental distances, where the atlas's kilometre-level
 * rounding would otherwise put whole coastal cities in the sea.
 */

import type { AdminAreasResponse } from "@parcelbound/contracts";
import { fillPolygon, LineSink, MeshSink, type Projection, type Vec2 } from "./geometry";
import { ADMIN1_BORDER, GRASS_BASE } from "./palette";

/** Same land colour as the atlas layer so swapping between them is seamless. */
const LAND = GRASS_BASE;
const COUNTRY_BORDER = 0x35442e;

/** Heights above the ocean plane. Above the atlas layers (40/90) so detail
 * wins wherever both are drawn; far enough apart to never z-fight. */
const LAND_Y = 44;
const ADMIN1_Y = 86;
const COUNTRY_Y = 94;

export interface AdminLabelPlace {
  name: string;
  kind: "admin1";
  lngLat: Vec2;
  /** Rough ground extent (metres, √area) — the label ladder uses it to show
   * big regions from far away and small boroughs only up close. */
  sizeM: number;
}

/** Approximate ground area of one lon/lat ring, in m² (equirectangular). */
function ringGroundAreaM2(ring: ReadonlyArray<Vec2>): number {
  if (ring.length < 3) return 0;
  let meanLat = 0;
  for (const [, lat] of ring) meanLat += lat / ring.length;
  const mPerDeg = 111_320;
  const kx = mPerDeg * Math.cos((meanLat * Math.PI) / 180);
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [ax, ay] = ring[i]!;
    const [bx, by] = ring[(i + 1) % ring.length]!;
    sum += ax * kx * (by * mPerDeg) - bx * kx * (ay * mPerDeg);
  }
  return Math.abs(sum) / 2;
}

export interface AdminMapBuild {
  fills: MeshSink;
  borders: LineSink;
  places: AdminLabelPlace[];
}

/** One build per response; the caller turns the sinks into meshes on a fresh
 * group. Country outlines double as the detailed coastline; region rings are
 * border-only so the land fill never doubles up. */
export function buildAdminAreas(resp: AdminAreasResponse, proj: Projection): AdminMapBuild {
  const fills = new MeshSink();
  const borders = new LineSink();
  const places: AdminLabelPlace[] = [];

  for (const area of resp.areas) {
    if (area.level === 0) {
      for (const ring of area.rings) {
        fillPolygon(fills, ring, proj, LAND_Y, LAND);
        borders.ring(ring, proj, COUNTRY_Y, COUNTRY_BORDER);
      }
    } else {
      let areaM2 = 0;
      for (const ring of area.rings) {
        borders.ring(ring, proj, ADMIN1_Y, ADMIN1_BORDER);
        areaM2 += ringGroundAreaM2(ring);
      }
      places.push({
        name: area.name,
        kind: "admin1",
        lngLat: area.labelPoint,
        sizeM: Math.sqrt(areaM2),
      });
    }
  }
  return { fills, borders, places };
}
